'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Command as CommandPrimitive } from 'cmdk';
import { useComposedRefs } from 'radix-ui/internal';
import { tv } from 'tailwind-variants';
import { faSearch, faSpinner } from '@icons/classic/regular';
import clsxm from '../utils/style';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './Dialog';

// COMMAND
const command = tv({
  base: `
    text-popover-foreground
    border-border-secondary
    flex
    h-full
    w-full
    flex-col
    overflow-hidden
    rounded-md
  `
});
function Command(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = command({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CommandPrimitive className={t1} data-slot="command" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// COMMAND DIALOG
const commandDialog = tv({
  base: `
    **:[[cmdk-group-heading]]:text-muted-foreground
    **:data-[slot=command-input-wrapper]:h-12
    **:[[cmdk-group-heading]]:px-2
    **:[[cmdk-group-heading]]:font-medium
    **:[[cmdk-group]]:px-2
    **:[[cmdk-group]:not([hidden])~[cmdk-group]]:pt-0
    **:[[cmdk-input-wrapper]_svg]:h-5
    **:[[cmdk-input-wrapper]_svg]:w-5
    **:[[cmdk-input]]:h-12
    **:[[cmdk-item]]:px-2
    **:[[cmdk-item]]:py-3
    **:[[cmdk-item]_svg]:h-5
    **:[[cmdk-item]_svg]:w-5
  `
});
function CommandDialog(t0) {
  const $ = _c(21);
  let children;
  let commandProps;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      title: t1,
      description: t2,
      children,
      commandProps,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = commandProps;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    commandProps = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const title = t1 === undefined ? "Command Palette" : t1;
  const description = t2 === undefined ? "Search for a command to run..." : t2;
  let t3;
  if ($[6] !== title) {
    t3 = <DialogTitle>{title}</DialogTitle>;
    $[6] = title;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== description) {
    t4 = <DialogDescription>{description}</DialogDescription>;
    $[8] = description;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <DialogHeader className="sr-only">{t3}{t4}</DialogHeader>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = commandDialog();
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== children || $[15] !== commandProps) {
    t7 = <DialogContent className="overflow-hidden p-0"><Command className={t6} {...commandProps}>{children}</Command></DialogContent>;
    $[14] = children;
    $[15] = commandProps;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== props || $[18] !== t5 || $[19] !== t7) {
    t8 = <Dialog {...props}>{t5}{t7}</Dialog>;
    $[17] = props;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}

// COMMAND INPUT
const commandInput = tv({
  slots: {
    wrapper: `
      border-border-secondary
      flex
      h-9
      items-center
      gap-2
      border-b
      px-3
    `,
    input: `
      placeholder:text-muted-foreground
      flex
      h-10
      w-full
      rounded-md
      bg-transparent
      py-3
      text-sm
      outline-hidden
      disabled:cursor-not-allowed
      disabled:opacity-50
    `
  }
});
function CommandInput(t0) {
  const $ = _c(37);
  let classNames;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      classNames,
      isSearching: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = classNames;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
  } else {
    classNames = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
  }
  const isSearching = t1 === undefined ? false : t1;
  const internalRef = React.useRef(null);
  const composedRef = useComposedRefs(internalRef, ref);
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[5] !== classNames?.input || $[6] !== classNames?.wrapper || $[7] !== isSearching || $[8] !== props) {
    const {
      wrapper,
      input
    } = commandInput();
    t4 = wrapper({
      className: classNames?.wrapper
    });
    t5 = "command-input-wrapper";
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = () => internalRef.current?.focus();
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    const t9 = isSearching && "animate-spin";
    let t10;
    if ($[18] !== t9) {
      t10 = clsxm("size-4 shrink-0 opacity-50", t9);
      $[18] = t9;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    const t11 = isSearching ? faSpinner : faSearch;
    if ($[20] !== t10 || $[21] !== t11) {
      t7 = <FontAwesomeIcon className={t10} icon={t11} />;
      $[20] = t10;
      $[21] = t11;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    const t12 = isSearching ? "Searching" : "";
    if ($[23] !== t12) {
      t8 = <span className="sr-only" role="status">{t12}</span>;
      $[23] = t12;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    T0 = CommandPrimitive.Input;
    t2 = props;
    t3 = input({
      className: classNames?.input
    });
    $[5] = classNames?.input;
    $[6] = classNames?.wrapper;
    $[7] = isSearching;
    $[8] = props;
    $[9] = T0;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    T0 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
  }
  let t9;
  if ($[25] !== T0 || $[26] !== composedRef || $[27] !== t2 || $[28] !== t3) {
    t9 = <T0 {...t2} className={t3} data-slot="command-input" ref={composedRef} />;
    $[25] = T0;
    $[26] = composedRef;
    $[27] = t2;
    $[28] = t3;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t4 || $[31] !== t5 || $[32] !== t6 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t10 = <div className={t4} data-slot={t5} onClick={t6}>{t7}{t8}{t9}</div>;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  return t10;
}

// COMMAND LIST
const commandList = tv({
  base: `
    max-h-[300px]
    scroll-py-1
    overflow-x-hidden
    overflow-y-auto
  `
});
function CommandList(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = commandList({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CommandPrimitive.List className={t1} data-slot="command-list" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// COMMAND EMPTY
const commandEmpty = tv({
  base: `
    py-4
    px-2
    text-center
    text-sm
  `
});
function CommandEmpty(t0) {
  const $ = _c(5);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = commandEmpty();
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props) {
    t2 = <CommandPrimitive.Empty className={t1} data-slot="command-empty" {...props} />;
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// COMMAND GROUP
const commandGroup = tv({
  base: `
    text-foreground
    **:[[cmdk-group-heading]]:text-muted-foreground
    overflow-hidden
    p-1
    **:[[cmdk-group-heading]]:px-2
    **:[[cmdk-group-heading]]:py-1.5
    **:[[cmdk-group-heading]]:text-xs
    **:[[cmdk-group-heading]]:font-medium
  `
});
function CommandGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = commandGroup({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CommandPrimitive.Group className={t1} data-slot="command-group" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// COMMAND SEPARATOR
const commandSeparator = tv({
  base: `
    bg-border-secondary
    -mx-1
    h-px
  `
});
function CommandSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = commandSeparator({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CommandPrimitive.Separator className={t1} data-slot="command-separator" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// COMMAND ITEM
const commandItem = tv({
  base: `
    data-[selected=true]:bg-background-tertiary
    data-[selected=true]:text-content-primary
    relative
    flex
    cursor-default
    items-center
    gap-2
    rounded-sm
    px-2
    py-1.5
    text-sm
    outline-hidden
    select-none
    data-[disabled=true]:pointer-events-none
    data-[disabled=true]:opacity-50
    **:[svg]:pointer-events-none
    **:[svg]:shrink-0
    **:[svg:not([class*='size-'])]:size-4
  `
});
function CommandItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = commandItem({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CommandPrimitive.Item className={t1} data-slot="command-item" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// COMMAND SHORTCUT
const commandShortcut = tv({
  base: `
    text-muted-foreground
    ml-auto
    text-xs
    tracking-widest
  `
});
function CommandShortcut(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = commandShortcut({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <span className={t1} data-slot="command-shortcut" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { command, Command, commandDialog, CommandDialog, commandEmpty, CommandEmpty, commandGroup, CommandGroup, commandInput, CommandInput, commandItem, CommandItem, commandList, CommandList, commandSeparator, CommandSeparator, commandShortcut, CommandShortcut };